<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import { storeToRefs } from 'pinia'
import { useCart } from '../stores/cartStore'
import { useCurrencyStore } from '../stores/currencyStore'
import { useLegalLinks } from '../router/legalLinks'

const { itemCount: cartCount, clearCart } = useCart()
const route = useRoute()
const router = useRouter()
const legalPaths = useLegalLinks()

const scrollToHash = (hash) => {
  if (route.path === '/') {
    const el = document.querySelector(hash)
    if (el) el.scrollIntoView({ behavior: 'smooth' })
  } else {
    router.push({ path: '/', hash })
  }
}
const currencyStore = useCurrencyStore()
const { currencies, selectedCode, selectedCurrency, symbol, rate } = storeToRefs(currencyStore)
const paymentConfig = ref(null)
const paymentConfigError = ref('')
const billingCountries = ref([])
const minimumTopUp = computed(() => {
  const configured = Number(selectedCurrency.value?.minDeposit)
  if (configured > 0) return configured
  const minimumEur = Number(paymentConfig.value?.minDepositEur)
  const selectedRate = Number(rate.value)
  return minimumEur > 0 && selectedRate > 0 ? minimumEur * selectedRate : null
})
const user = ref(null)
const withdrawalsEnabled = ref(true)
const showProfile = ref(false)
const showTopUp = ref(false)
const showLoginModal = ref(false)
const showMobileMenu = ref(false)
const showCurrencyMenu = ref(false)
const showHeaderCurrency = ref(false)

const toggleMobileMenu = () => {
  showMobileMenu.value = !showMobileMenu.value
}

const closeMobileMenu = () => {
  showMobileMenu.value = false
}
const loginAgreed = ref(false)
const loginMode = ref('steam') // 'steam' | 'email-login' | 'email-register'
const emailForm = reactive({ email: '', password: '', confirmPassword: '' })
const emailError = ref('')
const emailLoading = ref(false)
const topUpAmount = ref('')
const API_URL = import.meta.env.VITE_API_URL
const topUpError = ref('')
const topUpSuccess = ref('')
const topUpStep = ref(1)
const topUpLoading = ref(false)
const checkoutPaymentMethods = ref([])
const visibleCheckoutPaymentMethods = computed(() => checkoutPaymentMethods.value.filter((method) => {
  const allowed = Array.isArray(method.currencies) ? method.currencies : []
  return allowed.length === 0 || allowed.includes(selectedCode.value)
}))
const checkoutAgreed = ref(false)
const checkout = ref({
  firstName: '',
  lastName: '',
  email: '',
  country: '',
  city: '',
  address: '',
  zipCode: '',
  phone: ''
})
const loginSettings = ref({
  welcomeTitle: 'WELCOME TO ARK',
  ageText: 'I confirm that I am 18 years old or older and I agree to the',
  termsText: 'Terms of Use',
  privacyText: 'Privacy Notice'
})

const quickAmounts = [10, 50, 100, 200]
const selectedQuickAmount = ref(null)

const selectAmount = (amount) => {
  topUpAmount.value = Math.round(Number(amount) * Number(rate.value || 1) * 100) / 100
  selectedQuickAmount.value = amount
}

const onlyPositiveNumbers = (e) => {
  if (e.key === '-' || e.key === 'e' || e.key === '+') {
    e.preventDefault()
  }
}

const filterAmount = () => {
  const normalized = String(topUpAmount.value).replace(',', '.').replace(/[^\d.]/g, '')
  const [whole = '', ...fractions] = normalized.split('.')
  topUpAmount.value = fractions.length ? `${whole}.${fractions.join('').slice(0, 2)}` : whole
  // Сбрасываем quick select при ручном вводе
  selectedQuickAmount.value = null
}

const openTopUp = () => {
  showTopUp.value = true
  showProfile.value = false
}

const closeTopUp = () => {
  showTopUp.value = false
  topUpAmount.value = ''
  selectedQuickAmount.value = null
  topUpError.value = ''
  topUpSuccess.value = ''
  topUpStep.value = 1
  topUpLoading.value = false
  checkoutAgreed.value = false
  checkout.value = {
    firstName: '',
    lastName: '',
    email: '',
    country: '',
    city: '',
    address: '',
    zipCode: '',
    phone: ''
  }
}

const displayTopUpRate = () => {
  return `${symbol.value}${Number(rate.value || 1).toFixed(2)}`
}

const formatQuickAmount = (amountEur) => {
  return `${symbol.value}${(Number(amountEur) * Number(rate.value || 1)).toFixed(2)}`
}

const currencyLabel = () => {
  const match = currencies.value.find(c => c.code === selectedCode.value)
  if (!match) return `${selectedCode.value} ${symbol.value}`
  return `${match.code} ${match.symbol}`
}

const toggleCurrencyMenu = () => {
  showCurrencyMenu.value = !showCurrencyMenu.value
}

const toggleHeaderCurrency = () => {
  showHeaderCurrency.value = !showHeaderCurrency.value
}

const selectCurrency = (code) => {
  currencyStore.setCurrency(code)
  showCurrencyMenu.value = false
  showHeaderCurrency.value = false
}

watch(rate, () => {
  if (selectedQuickAmount.value !== null) selectAmount(selectedQuickAmount.value)
})

const openCheckout = () => {
  topUpError.value = ''
  topUpSuccess.value = ''

  const token = localStorage.getItem('steam_token')
  if (!token) {
    closeTopUp()
    openLoginModal()
    return
  }

  const amount = Number(topUpAmount.value)
  if (!(minimumTopUp.value > 0)) {
    topUpError.value = paymentConfigError.value || 'Top-up configuration is unavailable'
    return
  }
  if (!amount || amount < minimumTopUp.value) {
    topUpError.value = `Enter at least ${symbol.value}${minimumTopUp.value.toFixed(2)}`
    return
  }

  topUpStep.value = 2
}

const backToAmount = () => {
  topUpError.value = ''
  topUpSuccess.value = ''
  topUpStep.value = 1
}

const displayCheckoutTotal = () => {
  const amount = Number(topUpAmount.value)
  if (!amount || amount <= 0) return `${symbol.value}0`
  return `${symbol.value}${amount.toFixed(2)}`
}

const onDocClick = (e) => {
  const el = e.target
  if (!(el instanceof Element)) return
  if (el.closest('.topup-currency-wrap')) return
  showCurrencyMenu.value = false
  if (!el.closest('.header-currency-wrap')) {
    showHeaderCurrency.value = false
  }
}

const canSubmitCheckout = () => {
  const amount = Number(topUpAmount.value)
  if (!amount || amount <= 0) return false
  if (!checkout.value.firstName.trim()) return false
  if (!checkout.value.lastName.trim()) return false
  if (!checkout.value.email.trim()) return false
  if (!checkout.value.country.trim()) return false
  if (!checkout.value.city.trim()) return false
  if (!checkout.value.address.trim()) return false
  if (!checkout.value.zipCode.trim()) return false
  if (!checkout.value.phone.trim()) return false
  if (!checkoutAgreed.value) return false
  return true
}

const submitCheckout = async (method) => {
  topUpError.value = ''
  topUpSuccess.value = ''

  if (!canSubmitCheckout()) {
    topUpError.value = 'Please fill all fields and accept terms'
    return
  }

  const token = localStorage.getItem('steam_token')
  if (!token) {
    topUpError.value = 'Please log in first'
    return
  }

  topUpLoading.value = true

  try {
    const amount = Number(topUpAmount.value)
    const response = await axios.post(`${API_URL}/api/payments/initiate`, {
      customAmount: amount,
      gatewayId: method.id,
      billingInfo: checkout.value,
      currency: selectedCode.value
    }, {
      headers: { Authorization: `Bearer ${token}` }
    })

    if (response.data.success && response.data.redirectUrl) {
      window.location.href = response.data.redirectUrl
    } else {
      topUpError.value = response.data.message || 'Failed to initiate payment'
      topUpLoading.value = false
    }
  } catch (err) {
    topUpError.value = err.response?.data?.message || 'Payment initiation failed'
    topUpLoading.value = false
  }
}

onMounted(async () => {
  currencyStore.fetchCurrencies()
  try {
    const response = await axios.get(`${API_URL}/api/countries`)
    billingCountries.value = Array.isArray(response.data?.countries)
      ? response.data.countries
      : []
  } catch {
    billingCountries.value = []
  }
  try {
    const response = await axios.get(`${API_URL}/api/payments/config`)
    paymentConfig.value = response.data
  } catch {
    paymentConfig.value = null
    paymentConfigError.value = 'Top-up configuration is temporarily unavailable'
  }
  try {
    const response = await axios.get(`${API_URL}/api/payments/checkout-methods`)
    checkoutPaymentMethods.value = Array.isArray(response.data?.methods)
      ? response.data.methods
      : []
  } catch {
    checkoutPaymentMethods.value = []
  }
  const token = localStorage.getItem('steam_token')
  if (token) {
    try {
      const response = await axios.get(`${API_URL}/api/auth/user`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (response.data.user) {
        user.value = response.data.user
      }
    } catch (e) {
      localStorage.removeItem('steam_token')
    }
  }

  // Fetch withdrawal config so we can hide the AT badge when admin disabled payouts
  try {
    const cfg = await axios.get(`${API_URL}/api/withdrawals/config`)
    withdrawalsEnabled.value = cfg.data?.enabled !== false
  } catch {
    withdrawalsEnabled.value = false
  }
  
  // Load login modal settings
  try {
    const response = await axios.get(`${API_URL}/api/settings`)
    if (response.data) {
      if (response.data.loginWelcomeTitle) loginSettings.value.welcomeTitle = response.data.loginWelcomeTitle
      if (response.data.loginAgeText) loginSettings.value.ageText = response.data.loginAgeText
      if (response.data.loginTermsText) loginSettings.value.termsText = response.data.loginTermsText
      if (response.data.loginPrivacyText) loginSettings.value.privacyText = response.data.loginPrivacyText
    }
  } catch (e) {}
  
  // Listen for open-topup event from other components
  window.addEventListener('open-topup', openTopUp)
  window.addEventListener('open-login', openLoginModal)
  document.addEventListener('click', onDocClick)
})

onUnmounted(() => {
  window.removeEventListener('open-topup', openTopUp)
  window.removeEventListener('open-login', openLoginModal)
  document.removeEventListener('click', onDocClick)
})

const openLoginModal = () => {
  loginAgreed.value = false
  showLoginModal.value = true
}

const closeLoginModal = () => {
  showLoginModal.value = false
  loginAgreed.value = false
  loginMode.value = 'steam'
  emailForm.email = ''
  emailForm.password = ''
  emailForm.confirmPassword = ''
  emailError.value = ''
}

const loginWithSteam = () => {
  if (!loginAgreed.value) return
  closeLoginModal()
  window.location.href = `${API_URL}/api/auth/steam`
}

const testLogin = async () => {
  try {
    const res = await axios.post(`${API_URL}/api/auth/test-login`)
    if (res.data.success && res.data.token) {
      localStorage.setItem('steam_token', res.data.token)
      window.location.href = '/'
    }
  } catch (e) {
    console.error('Test login failed', e)
  }
}

const handleEmailLogin = async () => {
  if (!loginAgreed.value) return
  emailError.value = ''
  emailLoading.value = true
  try {
    const response = await axios.post(`${API_URL}/api/auth/email-login`, {
      email: emailForm.email,
      password: emailForm.password
    })
    if (response.data.token) {
      localStorage.setItem('steam_token', response.data.token)
      closeLoginModal()
      window.location.reload()
    }
  } catch (err) {
    emailError.value = err.response?.data?.error || 'Login failed'
  } finally {
    emailLoading.value = false
  }
}

const handleEmailRegister = async () => {
  if (!loginAgreed.value) return
  emailError.value = ''
  if (emailForm.password !== emailForm.confirmPassword) {
    emailError.value = 'Passwords do not match'
    return
  }
  if (emailForm.password.length < 6) {
    emailError.value = 'Password must be at least 6 characters'
    return
  }
  emailLoading.value = true
  try {
    const response = await axios.post(`${API_URL}/api/auth/register`, {
      email: emailForm.email,
      password: emailForm.password
    })
    if (response.data.token) {
      localStorage.setItem('steam_token', response.data.token)
      closeLoginModal()
      window.location.reload()
    }
  } catch (err) {
    emailError.value = err.response?.data?.error || 'Registration failed'
  } finally {
    emailLoading.value = false
  }
}

const connectSteam = () => {
  const token = localStorage.getItem('steam_token')
  if (token) {
    window.location.href = `${API_URL}/api/auth/steam/link?token=${token}`
  }
}

const showDeleteForm = ref(false)
const deletePassword = ref('')
const deleteLoading = ref(false)
const deleteError = ref('')

const handleDeleteAccount = async () => {
  if (!confirm('Are you sure you want to delete your account? This action cannot be undone.')) return
  deleteLoading.value = true
  deleteError.value = ''
  try {
    const token = localStorage.getItem('steam_token')
    await axios.delete(`${API_URL}/api/auth/delete-account`, {
      headers: { Authorization: `Bearer ${token}` },
      data: { password: deletePassword.value }
    })
    localStorage.removeItem('steam_token')
    clearCart()
    window.location.href = '/'
  } catch (err) {
    deleteError.value = err.response?.data?.error || 'Failed to delete account'
  } finally {
    deleteLoading.value = false
  }
}

const logout = () => {
  localStorage.removeItem('steam_token')
  clearCart()
  user.value = null
  showProfile.value = false
}

const openProfile = () => {
  showProfile.value = true
}

const closeProfile = () => {
  showProfile.value = false
}

const goToProfile = () => {
  showProfile.value = false
  window.location.href = '/profile'
}
</script>

<template>
  <header class="header">
    <div class="header-inner">
      <RouterLink to="/" class="logo">
        <img src="/logo.png?v=2" alt="ARK Server" class="logo-img" />
      </RouterLink>

      <nav class="nav desktop-nav">
        <RouterLink to="/" class="nav-link">Home</RouterLink>
        <a class="nav-link" href="/#features" @click.prevent="scrollToHash('#features')">Features</a>
        <a class="nav-link" href="/#rules" @click.prevent="scrollToHash('#rules')">Rules</a>
        <a class="nav-link" href="/#how-it-works" @click.prevent="scrollToHash('#how-it-works')">Start Playing</a>
        <RouterLink to="/market" class="nav-link nav-link-market">Market</RouterLink>
      </nav>

      <div class="header-right">
        <!-- Global Currency Switcher -->
        <div class="header-currency-wrap" v-if="user && currencies.length > 1">
          <button class="header-currency-btn" @click="toggleHeaderCurrency">
            <span class="hc-symbol">{{ symbol }}</span>
            <span class="hc-arrow" :class="{ open: showHeaderCurrency }">▾</span>
          </button>
          <div class="header-currency-dropdown" v-if="showHeaderCurrency">
            <button
              v-for="c in currencies"
              :key="c.code"
              class="header-currency-option"
              :class="{ active: c.code === selectedCode }"
              @click="selectCurrency(c.code)"
            >
              <span class="hco-symbol">{{ c.symbol }}</span>
              <span class="hco-code">{{ c.code }}</span>
            </button>
          </div>
        </div>

        <!-- Cart Icon (only visible when items in cart) -->
        <RouterLink v-if="cartCount > 0" to="/cart" class="cart-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
            <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/>
          </svg>
          <span class="cart-badge">{{ cartCount }}</span>
        </RouterLink>

        <!-- Logged in user -->
        <div v-if="user" class="user-section">
          <div class="header-balance" @click="openTopUp">
            <span class="hb-amount">{{ parseFloat(Number(user.balance).toFixed(2)) }}</span>
            <span class="hb-pts">ArkGem</span>
            <span class="hb-plus">+</span>
          </div>
          <RouterLink v-if="withdrawalsEnabled" to="/profile" class="header-balance header-tokens" title="Activity Points">
            <span class="hb-amount hb-amount-at">{{ parseFloat(Number(user.activity_tokens || 0).toFixed(2)) }}</span>
            <span class="hb-pts hb-pts-at">AP</span>
          </RouterLink>
          <div class="user-menu" @click="goToProfile">
            <img :src="user.avatar" class="user-avatar" />
            <span class="user-name">{{ user.displayName }}</span>
          </div>
        </div>
        
        <!-- Login button -->
        <button v-else @click="openLoginModal" class="btn-steam">
          <span class="btn-steam-text">Login / Register</span>
        </button>

        <!-- Mobile Menu Button -->
        <button class="mobile-menu-btn" @click="toggleMobileMenu" :class="{ active: showMobileMenu }">
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </div>

    <!-- Mobile Navigation -->
    <div class="mobile-nav" :class="{ open: showMobileMenu }">
      <nav class="mobile-nav-links">
        <RouterLink to="/" class="mobile-link" @click="closeMobileMenu">HOME</RouterLink>
        <a class="mobile-link" @click.prevent="closeMobileMenu(); scrollToHash('#features')">FEATURES</a>
        <a class="mobile-link" @click.prevent="closeMobileMenu(); scrollToHash('#rules')">RULES</a>
        <RouterLink to="/market" class="mobile-link market" @click="closeMobileMenu">SHOP</RouterLink>
        <a class="mobile-link" @click.prevent="closeMobileMenu(); scrollToHash('#how-it-works')">HOW IT WORKS</a>
      </nav>
      
      <div class="mobile-bottom">
        <div v-if="user" class="mobile-user-section">
          <!-- User Profile Card -->
          <RouterLink to="/profile" class="mobile-user-card" @click="closeMobileMenu">
            <img :src="user.avatar" class="mobile-user-avatar" />
            <span class="mobile-user-name">{{ user.displayName }}</span>
          </RouterLink>
          
          <!-- Balance & Cart Row -->
          <div class="mobile-actions-row">
            <div class="mobile-balance" @click="openTopUp(); closeMobileMenu()">
              <span class="balance-value">{{ parseFloat(Number(user.balance).toFixed(2)) }}</span>
              <span class="balance-label">ArkGem</span>
              <div class="balance-plus">+</div>
            </div>

            <RouterLink v-if="withdrawalsEnabled" to="/profile" class="mobile-balance mobile-tokens" @click="closeMobileMenu" title="Activity Points">
              <span class="balance-value">{{ parseFloat(Number(user.activity_tokens || 0).toFixed(2)) }}</span>
              <span class="balance-label">AP</span>
            </RouterLink>

            <RouterLink v-if="cartCount > 0" to="/cart" class="mobile-cart" @click="closeMobileMenu">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/>
              </svg>
              <span class="cart-count">{{ cartCount }}</span>
            </RouterLink>
          </div>
        </div>
        
        <!-- Для незалогиненных: корзина + кнопка логина -->
        <div v-else class="mobile-guest-section">
          <RouterLink v-if="cartCount > 0" to="/cart" class="mobile-cart-guest" @click="closeMobileMenu">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/>
            </svg>
            <span class="cart-label">Cart</span>
            <span class="cart-count">{{ cartCount }}</span>
          </RouterLink>
          
          <button @click="openLoginModal(); closeMobileMenu()" class="mobile-login-btn">
            <img src="/download.svg" alt="Steam" width="22" height="22">
            <span>LOGIN WITH STEAM</span>
          </button>
        </div>
      </div>
    </div>
  </header>

  <!-- Top Up Modal -->
  <Teleport to="body">
    <div v-if="showTopUp" class="topup-overlay" @click.self="closeTopUp">
      <div class="topup-modal" :class="{ 'has-checkout': topUpStep === 2 }">
        <button v-if="topUpStep === 1" class="topup-close" @click="closeTopUp">×</button>

        <div v-if="topUpStep === 1">
          <h2 class="topup-title">TOP UP</h2>
        </div>

        <div v-if="topUpStep === 1">
          <div class="topup-form">
            <label class="topup-label">Enter your amount:</label>
            <input 
              type="text" 
              inputmode="decimal"
              v-model="topUpAmount" 
              class="topup-input" 
              placeholder="Enter amount"
              @keypress="onlyPositiveNumbers"
              @input="filterAmount"
            />
            <div class="topup-currency-wrap" v-if="currencies.length">
              <button type="button" class="topup-currency" @click="toggleCurrencyMenu">
                <span class="topup-currency-label">{{ currencyLabel() }}</span>
                <span class="topup-currency-arrow" :class="{ open: showCurrencyMenu }">▾</span>
              </button>
              <div class="topup-currency-menu" v-if="showCurrencyMenu">
                <button
                  v-for="c in currencies"
                  :key="c.code"
                  type="button"
                  class="topup-currency-item"
                  :class="{ active: c.code === selectedCode }"
                  @click="selectCurrency(c.code)"
                >
                  <span class="code">{{ c.code }}</span>
                  <span class="name">{{ c.name }}</span>
                  <span class="sym">{{ c.symbol }}</span>
                </button>
              </div>
            </div>
            <div class="topup-rate">
              <span>1 ArkGem = {{ displayTopUpRate() }}</span>
            </div>
          </div>
          
          <div class="topup-quick-label">Quick select:</div>
          <div class="topup-quick">
            <button 
              v-for="amount in quickAmounts" 
              :key="amount" 
              class="topup-quick-btn"
              :class="{ active: selectedQuickAmount === amount }"
              @click="selectAmount(amount)"
            >
              {{ amount }} AG
            </button>
          </div>

          <div v-if="topUpError" class="checkout-msg error" style="margin-top: 14px;">{{ topUpError }}</div>
          <div v-if="topUpSuccess" class="checkout-msg success" style="margin-top: 14px;">{{ topUpSuccess }}</div>

          <button class="btn-topup-submit" :disabled="!topUpAmount || !minimumTopUp" @click="openCheckout">
            <span>TOP UP</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>
        </div>

        <div v-if="topUpStep === 2" class="checkout-overlay">
          <div class="checkout-head">
            <button type="button" class="checkout-back" @click="backToAmount">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
            </button>
            <div class="checkout-head-title">ENTER CONTACT INFORMATION</div>
            <button type="button" class="checkout-x" @click="closeTopUp">×</button>
          </div>

          <div class="checkout-grid">
            <div class="checkout-field">
              <label>First name:</label>
              <input v-model="checkout.firstName" type="text" class="checkout-input" placeholder="Your first name" />
            </div>
            <div class="checkout-field">
              <label>Last name:</label>
              <input v-model="checkout.lastName" type="text" class="checkout-input" placeholder="Your last name" />
            </div>
            <div class="checkout-field">
              <label>Phone:</label>
              <input v-model="checkout.phone" type="text" class="checkout-input" placeholder="Your phone number" />
            </div>
            <div class="checkout-field">
              <label>Email:</label>
              <input v-model="checkout.email" type="email" class="checkout-input" placeholder="Your email" />
            </div>
            <div class="checkout-field">
              <label>Address:</label>
              <input v-model="checkout.address" type="text" class="checkout-input" placeholder="Your address" />
            </div>
            <div class="checkout-field">
              <label>City:</label>
              <input v-model="checkout.city" type="text" class="checkout-input" placeholder="Your city" />
            </div>
            <div class="checkout-field">
              <label>Country:</label>
              <select v-model="checkout.country" class="checkout-input">
                <option value="" disabled>Select country</option>
                <option
                  v-for="country in billingCountries"
                  :key="country.iso"
                  :value="country.iso"
                >
                  {{ country.name }}
                </option>
              </select>
            </div>
            <div class="checkout-field">
              <label>ZIP Code:</label>
              <input v-model="checkout.zipCode" type="text" class="checkout-input" placeholder="Your zip code" />
            </div>
          </div>

          <label class="checkout-agree">
            <input type="checkbox" v-model="checkoutAgreed" />
            <span class="check"></span>
            <span class="text">
              I agree to the
              <a :href="legalPaths.privacy" target="_blank" rel="noopener noreferrer" @click.stop>Privacy Notice</a>
              and
              <a :href="legalPaths.terms" target="_blank" rel="noopener noreferrer" @click.stop>Terms of Use</a>.
            </span>
          </label>

          <div class="checkout-actions">
            <div class="checkout-total">TOTAL: {{ displayCheckoutTotal() }} / {{ topUpAmount }} ArkGem</div>

            <div v-if="topUpError" class="checkout-msg error">{{ topUpError }}</div>
            <div v-if="topUpSuccess" class="checkout-msg success">{{ topUpSuccess }}</div>

            <p v-if="!canSubmitCheckout() && !topUpLoading" class="checkout-hint">Fill in all fields and accept terms to proceed</p>
            <div v-if="visibleCheckoutPaymentMethods.length" class="checkout-methods">
              <button
                v-for="method in visibleCheckoutPaymentMethods"
                :key="method.id"
                class="checkout-pay"
                :class="{ active: canSubmitCheckout() }"
                :disabled="!canSubmitCheckout() || topUpLoading"
                @click="submitCheckout(method)"
              >
                <span v-if="method.logos?.length" class="checkout-method-logos">
                  <img
                    v-for="(logo, index) in method.logos"
                    :key="index"
                    :src="logo"
                    :alt="method.name"
                    class="checkout-method-logo"
                  />
                </span>
                <span>{{ topUpLoading ? 'PROCESSING...' : method.name }}</span>
              </button>
            </div>
            <p v-else class="checkout-hint">No payment method is available for {{ selectedCode }}.</p>
          </div>
        </div>
      </div>
    </div>
  </Teleport>

    <!-- Login Modal -->
    <Teleport to="body">
      <div v-if="showLoginModal" class="login-overlay" @click.self="closeLoginModal">
        <div class="login-modal">
          <button class="login-close" @click="closeLoginModal">×</button>

          <div class="login-corners">
            <span class="corner tl"></span>
            <span class="corner tr"></span>
            <span class="corner bl"></span>
            <span class="corner br"></span>
          </div>

          <h2 class="login-title">{{ loginSettings.welcomeTitle }}</h2>

          <label class="login-checkbox">
            <input type="checkbox" v-model="loginAgreed" />
            <span class="checkbox-custom"></span>
            <span class="checkbox-text">
              {{ loginSettings.ageText }}
              <a :href="legalPaths.terms" target="_blank" rel="noopener noreferrer" class="login-link" @click.stop>{{ loginSettings.termsText }}</a>
              and
              <a :href="legalPaths.privacy" target="_blank" rel="noopener noreferrer" class="login-link" @click.stop>{{ loginSettings.privacyText }}</a>
            </span>
          </label>

          <!-- Steam Login -->
          <button class="login-steam-btn" :disabled="!loginAgreed" @click="loginWithSteam">
            <img src="/download.svg" alt="Steam" width="24" height="24">
            LOGIN WITH STEAM
          </button>

          <div class="login-divider"><span>or</span></div>

          <!-- Email Login / Register -->
          <div class="login-email-section">
            <div v-if="emailError" class="login-email-error">{{ emailError }}</div>

            <input type="email" v-model="emailForm.email" placeholder="Email" class="login-email-input" />
            <input type="password" v-model="emailForm.password" placeholder="Password" class="login-email-input" />
            <input v-if="loginMode === 'email-register'" type="password" v-model="emailForm.confirmPassword" placeholder="Confirm Password" class="login-email-input" />

            <button
              v-if="loginMode !== 'email-register'"
              class="login-email-btn"
              :disabled="!loginAgreed || emailLoading"
              @click="handleEmailLogin"
            >
              {{ emailLoading ? 'Logging in...' : 'LOG IN WITH EMAIL' }}
            </button>
            <button
              v-else
              class="login-email-btn"
              :disabled="!loginAgreed || emailLoading"
              @click="handleEmailRegister"
            >
              {{ emailLoading ? 'Creating account...' : 'CREATE ACCOUNT' }}
            </button>

            <button class="login-mode-toggle" @click="loginMode = loginMode === 'email-register' ? 'email-login' : 'email-register'">
              {{ loginMode === 'email-register' ? 'Already have an account? Log In' : 'No account? Register' }}
            </button>
            <a v-if="loginMode === 'email-login'" href="/forgot-password" class="login-mode-toggle" style="display:block;margin-top:4px;font-size:0.8rem;color:#888;">Forgot password?</a>
          </div>
        </div>
      </div>
    </Teleport>
</template>

<style scoped>
.header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: var(--z-header);
    padding: var(--space-lg) var(--space-3xl);
    background: var(--color-bg-overlay);
    backdrop-filter: blur(10px);
    border-bottom: 1px solid var(--color-border);
}

.header-inner {
    max-width: var(--container-max);
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
}

/* Logo */
.logo {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
}

.desktop-nav {
    flex: 1 1 auto;
    justify-content: center;
    min-width: 0;
}

.logo-img {
    height: 55px;
    width: auto;
    transition: transform 0.3s ease, filter 0.3s ease;
    filter: drop-shadow(0 0 8px rgba(0, 212, 255, 0.3));
}

.logo:hover .logo-img {
    transform: scale(1.08);
    filter: drop-shadow(0 0 15px rgba(0, 212, 255, 0.6));
}

/* Navigation */
.nav {
    display: flex;
    gap: 40px;
}

@media (max-width: 900px) {
    .nav {
        display: none !important;
    }
    
    .mobile-menu-btn {
        display: flex !important;
    }
}

.nav-link {
    color: var(--color-text-muted);
    font-weight: 600;
    font-size: var(--text-base);
    text-transform: uppercase;
    letter-spacing: 2px;
    padding-left: 2px;
    position: relative;
    cursor: pointer;
}

.nav-link::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    right: 0;
    margin: 0 auto;
    width: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--color-primary), var(--color-accent));
    border-radius: 2px;
    transition: width 0.3s ease;
}

.nav-link:hover,
.nav-link.active {
    color: var(--color-primary);
}

.nav-link:hover::after,
.nav-link.active::after {
    width: 100%;
}

.nav-link-market {
    color: var(--color-primary);
}

.nav-link-market.router-link-active {
    color: var(--color-primary);
}

.nav-link-market.router-link-active::after {
    width: 100%;
}

.nav-link-trading {
    color: #2ecc71;
}

.nav-link-trading.router-link-active {
    color: #2ecc71;
}

.nav-link-trading.router-link-active::after {
    width: 100%;
    background: linear-gradient(90deg, #2ecc71, #27ae60);
}

/* Header Right */
.header-right {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
}

/* Cart Icon */
.cart-icon {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.7);
    transition: all 0.3s ease;
    padding: 6px;
}

.cart-icon svg {
    width: 26px;
    height: 26px;
}

.cart-icon:hover {
    color: var(--color-primary);
    filter: drop-shadow(0 0 6px rgba(0, 212, 255, 0.4));
}

.cart-badge {
    position: absolute;
    top: -6px;
    right: -6px;
    min-width: 20px;
    height: 20px;
    background: linear-gradient(135deg, var(--color-primary) 0%, #0095b3 100%);
    color: #000;
    font-size: 0.7rem;
    font-weight: 800;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 5px;
    box-shadow: 0 2px 10px rgba(0, 212, 255, 0.4);
}

.btn-steam {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 28px;
    background: transparent;
    border: none;
    color: white;
    font-family: var(--font-primary);
    font-weight: 600;
    font-size: var(--text-sm);
    text-transform: uppercase;
    letter-spacing: 2px;
    text-decoration: none;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
}

.btn-steam::before,
.btn-steam::after {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    border: 2px solid var(--color-primary);
    transition: all 0.3s ease;
}

.btn-steam::before {
    top: 0;
    left: 0;
    border-right: none;
    border-bottom: none;
}

.btn-steam::after {
    bottom: 0;
    right: 0;
    border-left: none;
    border-top: none;
}

.btn-steam:hover::before,
.btn-steam:hover::after {
    width: 20px;
    height: 20px;
    border-color: var(--color-accent);
}

.btn-steam:hover {
    color: var(--color-primary);
}

.btn-steam img {
    width: 26px;
    height: 26px;
    filter: brightness(1.2);
}

/* User Menu */
.user-menu {
    display: flex;
    align-items: center;
    gap: 12px;
}

.user-avatar {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    border: 2px solid var(--color-primary);
}

.user-name {
    color: var(--color-text);
    font-weight: 600;
    font-size: var(--text-sm);
}

.user-section {
    display: flex;
    align-items: center;
    gap: 10px;
}

.header-balance {
    display: flex;
    align-items: center;
    gap: 6px;
    background: rgba(0, 212, 255, 0.1);
    border: 1px solid rgba(0, 212, 255, 0.3);
    border-radius: 20px;
    padding: 8px 12px 8px 16px;
    cursor: pointer;
    transition: all 0.2s;
}

.header-balance:hover {
    background: rgba(0, 212, 255, 0.15);
    border-color: var(--color-primary);
}

.header-tokens {
    text-decoration: none;
    padding-right: 12px;
    background: rgba(56, 189, 248, 0.10);
    border: 1px solid rgba(56, 189, 248, 0.32);
}
.header-tokens:hover {
    background: rgba(56, 189, 248, 0.18);
    border-color: rgba(125, 211, 252, 0.7);
}
.hb-amount-at {
    color: #7dd3fc;
}
.hb-pts-at {
    color: rgba(125, 211, 252, 0.65);
}

.hb-amount {
    font-weight: 700;
    font-size: 1rem;
    color: var(--color-primary);
}

.hb-pts {
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.5);
    text-transform: uppercase;
}

.hb-plus {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    background: var(--color-primary);
    color: #000;
    border-radius: 50%;
    font-size: 0.9rem;
    font-weight: 700;
    margin-left: 4px;
}

/* Header Currency Switcher */
.header-currency-wrap {
    position: relative;
    align-self: stretch;
}

.header-currency-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    background: rgba(0, 212, 255, 0.1);
    border: 1px solid rgba(0, 212, 255, 0.3);
    border-radius: 20px;
    padding: 8px 12px 8px 16px;
    cursor: pointer;
    transition: all 0.2s;
    color: #fff;
    font-size: 0.85rem;
    font-weight: 600;
    height: 100%;
}

.header-currency-btn:hover {
    background: rgba(0, 212, 255, 0.15);
    border-color: var(--color-primary);
}

.hc-symbol {
    color: var(--color-primary);
    font-weight: 700;
    font-size: 1rem;
}

.hc-arrow {
    color: rgba(255, 255, 255, 0.4);
    font-size: 0.7rem;
    transition: transform 0.25s ease;
    margin-left: 2px;
}

.hc-arrow.open {
    transform: rotate(180deg);
}

.header-currency-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    min-width: 120px;
    z-index: 10001;
    border-radius: 14px;
    padding: 6px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    background: linear-gradient(180deg, rgba(10, 10, 16, 0.99), rgba(10, 10, 16, 0.97));
    border: 1px solid rgba(0, 212, 255, 0.22);
    box-shadow: 0 22px 70px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(0, 212, 255, 0.06) inset;
    backdrop-filter: blur(14px);
    animation: fadeIn 0.15s ease;
}

.header-currency-option {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-radius: 10px;
    background: transparent;
    border: 1px solid transparent;
    color: #eaeaea;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 0.85rem;
}

.header-currency-option:hover {
    border-color: rgba(0, 212, 255, 0.35);
    background: rgba(0, 212, 255, 0.08);
}

.header-currency-option.active {
    border-color: rgba(0, 212, 255, 0.55);
    background: linear-gradient(135deg, rgba(0, 212, 255, 0.18), rgba(0, 212, 255, 0.06));
}

.hco-symbol {
    font-weight: 800;
    color: var(--color-primary);
    min-width: 20px;
    text-align: center;
}

.hco-code {
    font-weight: 600;
    letter-spacing: 1px;
    color: #fff;
}

.btn-logout {
    background: transparent;
    border: 1px solid rgba(255, 100, 100, 0.3);
    color: #ff6464;
    padding: 8px 16px;
    border-radius: 6px;
    font-size: var(--text-xs);
    text-transform: uppercase;
    letter-spacing: 1px;
    cursor: pointer;
    transition: all 0.2s;
}

.user-menu {
    cursor: pointer;
    transition: opacity 0.2s;
}

.user-menu:hover {
    opacity: 0.8;
}

/* Profile Modal */
.profile-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    backdrop-filter: blur(5px);
}

.profile-modal {
    background: linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 100%);
    border: 2px solid #333;
    border-radius: 16px;
    padding: 2rem;
    width: 100%;
    max-width: 380px;
    text-align: center;
    position: relative;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.profile-close {
    position: absolute;
    top: 12px;
    right: 12px;
    background: none;
    border: none;
    color: #666;
    font-size: 28px;
    cursor: pointer;
    line-height: 1;
    transition: color 0.2s;
}

.profile-close:hover {
    color: #fff;
}

.profile-avatar {
    width: 120px;
    height: 120px;
    margin: 0 auto 1.5rem;
    border-radius: 50%;
    overflow: hidden;
    border: 3px solid var(--color-primary);
    box-shadow: 0 0 30px rgba(0, 212, 255, 0.3);
}

.profile-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.profile-info {
    margin-bottom: 1.5rem;
}

.profile-row-inline {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    font-size: 0.95rem;
}

.profile-separator {
    color: #444;
}

.profile-label {
    color: #888;
}

.profile-value {
    color: #fff;
    font-weight: 600;
}

.profile-id {
    color: var(--color-primary);
    font-family: monospace;
    font-size: 0.85rem;
}

.profile-balance {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin-bottom: 1.5rem;
    padding: 1rem;
    background: rgba(0, 0, 0, 0.3);
    border-radius: 12px;
}

.balance-icon {
    width: 24px;
    height: 24px;
    stroke: #888;
    fill: none;
    stroke-width: 2;
}

.balance-amount {
    font-size: 1.5rem;
    font-weight: 700;
    color: #fff;
}

.coin-icon {
    width: 24px;
    height: 24px;
    stroke: var(--color-accent);
    fill: none;
    stroke-width: 2;
}

.btn-topup {
    width: 100%;
    background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
    color: #000;
    border: none;
    padding: 14px 24px;
    border-radius: 8px;
    font-weight: 700;
    font-size: 1rem;
    letter-spacing: 1px;
    cursor: pointer;
    transition: all 0.2s;
    margin-bottom: 12px;
}

.btn-topup:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 25px rgba(0, 212, 255, 0.4);
}

.btn-view-profile {
    width: 100%;
    background: transparent;
    border: 1px solid var(--color-primary);
    color: var(--color-primary);
    padding: 12px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.9rem;
    letter-spacing: 1px;
    cursor: pointer;
    transition: all 0.2s;
    margin-bottom: 10px;
}

.btn-view-profile:hover {
    background: rgba(0, 212, 255, 0.1);
}

.btn-profile-logout {
    width: 100%;
    background: #4a4a4a;
    color: #fff;
    border: none;
    padding: 14px;
    border-radius: 8px;
    font-weight: 700;
    font-size: 1rem;
    letter-spacing: 2px;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-profile-logout:hover {
    background: #5a5a5a;
}

/* Top Up Modal */
.topup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    backdrop-filter: blur(5px);
    animation: fadeIn 0.3s ease;
    overflow-y: auto;
    padding: 20px;
}

@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

@keyframes slideUp {
    from { opacity: 0; transform: translateY(30px) scale(0.95); }
    to { opacity: 1; transform: translateY(0) scale(1); }
}

.topup-modal {
    background: linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 100%);
    border: 2px solid #333;
    border-radius: 16px;
    padding: 2rem;
    width: 100%;
    max-width: 400px;
    max-height: 90vh;
    overflow: visible;
    text-align: center;
    position: relative;
    animation: slideUp 0.4s ease;
    box-sizing: border-box;
}

.topup-modal.has-checkout {
    padding: 0;
    max-width: 680px;
    max-height: 90vh;
    overflow: visible;
    background: transparent;
    border: 0;
    border-radius: 24px;
}

.topup-modal.has-checkout .topup-close {
    z-index: 10;
}

.topup-close {
    position: absolute;
    top: 15px;
    right: 15px;
    background: none;
    border: none;
    color: #666;
    font-size: 28px;
    cursor: pointer;
    line-height: 1;
    transition: color 0.2s;
    z-index: 10;
    padding: 0;
    margin: 0;
}

.topup-close:hover {
    color: var(--color-primary);
}

.topup-title {
    font-family: var(--font-display);
    font-size: 1.75rem;
    color: var(--color-primary);
    margin-bottom: 1.5rem;
    letter-spacing: 2px;
    position: relative;
}

.topup-subtitle {
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.95rem;
    margin-bottom: 2rem;
}

.topup-form {
    margin-bottom: 0.75rem;
    overflow: visible;
}

.topup-modal * {
    box-sizing: border-box;
}

.topup-label {
    display: block;
    color: #888;
    margin-bottom: 0.4rem;
    font-size: 0.8rem;
}

.topup-input {
    width: 100%;
    background: rgba(0, 0, 0, 0.4);
    border: 2px solid rgba(255,255,255,0.1);
    border-radius: 50px;
    padding: 0.7rem 1.2rem;
    color: #fff;
    font-size: 1rem;
    text-align: center;
    transition: all 0.3s;
}

.topup-input:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 20px rgba(0, 212, 255, 0.2);
}

.topup-input::placeholder {
    color: #444;
}

.topup-currency {
    width: 100%;
    margin-top: 8px;
    background: rgba(0, 0, 0, 0.35);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px;
    color: #fff;
    padding: 8px 12px;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 1px;
    outline: none;
    transition: all 0.3s;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
}

.topup-currency:focus {
    border-color: var(--color-primary);
    box-shadow: 0 0 20px rgba(0, 212, 255, 0.2);
}

.topup-currency-wrap {
    position: relative;
}

.topup-currency-label {
    display: inline-flex;
    align-items: center;
    gap: 10px;
}

.topup-currency-arrow {
    opacity: 0.9;
    transition: transform 0.25s ease, opacity 0.25s ease;
}

.topup-currency-arrow.open {
    transform: rotate(180deg);
    opacity: 1;
}

.topup-currency-menu {
    position: absolute;
    left: 0;
    right: 0;
    top: calc(100% + 6px);
    z-index: 10010;
    border-radius: 14px;
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    background: linear-gradient(180deg, rgba(10, 10, 16, 0.99), rgba(10, 10, 16, 0.97));
    border: 1px solid rgba(0, 212, 255, 0.22);
    box-shadow: 0 22px 70px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(0, 212, 255, 0.06) inset;
    backdrop-filter: blur(14px);
}

.topup-currency-item {
    width: 100%;
    display: grid;
    grid-template-columns: 64px 1fr 40px;
    gap: 10px;
    align-items: center;
    padding: 12px 12px;
    border-radius: 12px;
    background: transparent;
    border: 1px solid transparent;
    color: #eaeaea;
    cursor: pointer;
    transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease;
    text-align: left;
}

.topup-currency-item .code {
    font-weight: 800;
    letter-spacing: 1px;
    color: #fff;
}

.topup-currency-item .name {
    color: rgba(255,255,255,0.62);
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.topup-currency-item .sym {
    text-align: right;
    font-weight: 800;
    color: var(--color-primary);
}

.topup-currency-item:hover {
    border-color: rgba(0, 212, 255, 0.35);
    background: rgba(0, 212, 255, 0.08);
    transform: translateY(-1px);
}

.topup-currency-item.active {
    border-color: rgba(0, 212, 255, 0.55);
    background: linear-gradient(135deg, rgba(0, 212, 255, 0.18), rgba(0, 212, 255, 0.06));
}

.checkout-block {
    margin-top: 1.25rem;
    padding: 16px;
    border-radius: 18px;
    background: linear-gradient(165deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02));
    border: 1px solid rgba(255,255,255,0.08);
    box-shadow: 0 18px 55px rgba(0,0,0,0.28);
}

.checkout-title {
    font-family: var(--font-display);
    font-weight: 800;
    letter-spacing: 2px;
    font-size: 0.95rem;
    color: rgba(255,255,255,0.9);
    margin-bottom: 12px;
}

.checkout-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.checkout-field.full {
    grid-column: 1 / -1;
}

.checkout-field label {
    display: block;
    font-size: 0.82rem;
    color: rgba(255,255,255,0.55);
    margin-bottom: 6px;
    letter-spacing: 0.5px;
}

.checkout-input {
    width: 100%;
    background: rgba(0, 0, 0, 0.35);
    border: 1px solid rgba(255,255,255,0.10);
    border-radius: 12px;
    padding: 12px 12px;
    color: #fff;
    font-size: 0.95rem;
    outline: none;
    transition: all 0.25s ease;
}

.checkout-input:focus {
    border-color: rgba(0, 212, 255, 0.55);
    box-shadow: 0 0 0 4px rgba(0, 212, 255, 0.10);
}

.checkout-input::placeholder {
    color: rgba(255,255,255,0.25);
}

.checkout-methods {
    display: grid;
    gap: 10px;
}

.checkout-methods .checkout-pay {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
}

.checkout-method-logos {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.checkout-method-logo {
    width: auto;
    height: 22px;
    object-fit: contain;
}

.pay-title {
    margin-top: 16px;
    font-size: 0.82rem;
    font-weight: 800;
    color: rgba(255,255,255,0.75);
    letter-spacing: 1.6px;
}

.pay-methods {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 10px;
    margin-top: 10px;
}

.pay-method {
    text-align: left;
    padding: 12px 12px;
    border-radius: 14px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.10);
    color: #fff;
    cursor: pointer;
    transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;
}

.pay-method:hover {
    transform: translateY(-2px);
    border-color: rgba(0, 212, 255, 0.35);
    background: rgba(0, 212, 255, 0.08);
}

.pay-method.active {
    border-color: rgba(0, 212, 255, 0.65);
    background: linear-gradient(135deg, rgba(0, 212, 255, 0.18), rgba(0, 212, 255, 0.06));
}

.pm-name {
    display: block;
    font-weight: 900;
    letter-spacing: 0.8px;
}

.pm-sub {
    display: block;
    margin-top: 4px;
    font-size: 0.78rem;
    color: rgba(255,255,255,0.55);
    font-weight: 600;
}

.checkout-msg {
    margin-top: 12px;
    padding: 10px 12px;
    border-radius: 12px;
    font-weight: 700;
    font-size: 0.9rem;
    border: 1px solid rgba(255,255,255,0.08);
}

.checkout-msg.error {
    background: rgba(255, 70, 70, 0.10);
    border-color: rgba(255, 70, 70, 0.25);
    color: rgba(255,255,255,0.92);
}

.checkout-msg.success {
    background: rgba(40, 220, 140, 0.10);
    border-color: rgba(40, 220, 140, 0.25);
    color: rgba(255,255,255,0.92);
}

.checkout-overlay {
    border-radius: 24px;
    background: linear-gradient(165deg, rgba(12, 18, 30, 0.98) 0%, rgba(5, 10, 20, 0.99) 100%);
    border: 1px solid rgba(0, 212, 255, 0.25);
    box-shadow: 0 25px 80px rgba(0, 0, 0, 0.6), 0 0 80px rgba(0, 212, 255, 0.15);
    padding: 20px 24px 24px;
    color: #fff;
    animation: checkout-in 0.18s ease-out;
    overflow-y: auto;
    max-height: 90vh;
}

.checkout-overlay .checkout-grid {
    max-width: 640px;
    margin: 0 auto;
}

.checkout-overlay .checkout-field {
    text-align: left;
}

@keyframes checkout-in {
    from { transform: translateY(10px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
}

.checkout-head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
    padding-bottom: 14px;
    border-bottom: 1px solid rgba(255,255,255,0.08);
}

.checkout-back {
    width: 34px;
    height: 34px;
    border-radius: 12px;
    border: 1px solid rgba(255,255,255,0.12);
    background: rgba(255,255,255,0.06);
    cursor: pointer;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.15s ease, background 0.15s ease;
}

.checkout-back svg {
    width: 18px;
    height: 18px;
}

.checkout-back:hover {
    transform: translateY(-1px);
    background: rgba(255,255,255,0.10);
}

.checkout-head-title {
    flex: 1;
    text-align: center;
    font-family: var(--font-display);
    font-weight: 900;
    letter-spacing: 2px;
    font-size: 1.05rem;
    color: rgba(255,255,255,0.92);
    text-transform: uppercase;
}

.checkout-x {
    width: 34px;
    height: 34px;
    border-radius: 12px;
    border: 1px solid rgba(255,255,255,0.12);
    background: rgba(255,255,255,0.06);
    cursor: pointer;
    font-size: 20px;
    font-weight: 300;
    line-height: 1;
    color: rgba(255,255,255,0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.15s ease, background 0.15s ease;
}

.checkout-x:hover {
    transform: translateY(-1px);
    background: rgba(255,255,255,0.10);
}

.checkout-overlay .checkout-grid {
    gap: 12px;
}

.checkout-overlay .checkout-field label {
    color: rgba(255,255,255,0.70);
    font-weight: 800;
}

.checkout-overlay .checkout-input {
    background: rgba(0, 0, 0, 0.45);
    border: 1px solid rgba(255,255,255,0.12);
    color: #fff;
    border-radius: 14px;
    padding: 14px 14px;
    font-weight: 650;
}

.checkout-overlay .checkout-input::placeholder {
    color: rgba(255,255,255,0.28);
}

.checkout-overlay .checkout-input:focus {
    border-color: rgba(0, 212, 255, 0.55);
    box-shadow: 0 0 0 4px rgba(0, 212, 255, 0.12);
}

select.checkout-input {
    appearance: none;
    -webkit-appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.5)' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 14px center;
    padding-right: 36px;
    cursor: pointer;
}

select.checkout-input option {
    background: #0c121e;
    color: #fff;
}

.checkout-agree {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 14px;
    font-size: 0.9rem;
    color: rgba(255,255,255,0.75);
    user-select: none;
    cursor: pointer;
    position: relative;
}

.checkout-agree input {
    width: 0;
    height: 0;
    opacity: 0;
    pointer-events: none;
    position: absolute;
    left: 0;
    top: 0;
}

.checkout-agree .check {
    width: 22px;
    height: 22px;
    border-radius: 7px;
    border: 2px solid rgba(255,255,255,0.25);
    background: rgba(0,0,0,0.25);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    flex: 0 0 22px;
}

.checkout-agree input:checked + .check {
    border-color: rgba(0, 212, 255, 0.9);
    background: linear-gradient(135deg, rgba(0, 212, 255, 0.35), rgba(0, 163, 204, 0.20));
    box-shadow: 0 0 0 4px rgba(0, 212, 255, 0.12);
}

.checkout-agree input:checked + .check::after {
    content: '✓';
    color: #fff;
    font-weight: 900;
    font-size: 14px;
}

.checkout-agree .text {
    line-height: 1.4;
}

.checkout-agree .text a {
  position: relative;
  z-index: 2;
  color: var(--accent, #dfc166);
  font-weight: 700;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 3px;
  pointer-events: auto;
  transition: color 0.18s ease, opacity 0.18s ease;
}

.checkout-agree .text a:hover,
.checkout-agree .text a:focus-visible {
  color: #fff2ad;
  opacity: 0.9;
}

.checkout-actions {
    margin-top: 20px;
}

.checkout-total {
    margin-top: 0;
    text-align: center;
    font-family: var(--font-display);
    font-weight: 900;
    letter-spacing: 2px;
    font-size: 1.45rem;
    color: #fff;
    text-shadow: 0 0 18px rgba(0, 212, 255, 0.15);
}

.checkout-hint {
    color: rgba(255, 255, 255, 0.35);
    font-size: 0.75rem;
    text-align: center;
    margin-top: 10px;
    letter-spacing: 0.5px;
}

.checkout-pay {
    width: 100%;
    margin-top: 10px;
    padding: 16px 18px;
    border-radius: 12px;
    border: none;
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.3);
    font-weight: 900;
    font-size: 0.9rem;
    letter-spacing: 2px;
    cursor: not-allowed;
    transition: all 0.3s ease;
}

.checkout-pay.active {
    background: linear-gradient(135deg, #00d4ff 0%, #0099cc 100%);
    color: #000;
    cursor: pointer;
    box-shadow: 0 8px 30px rgba(0, 212, 255, 0.4);
}

.checkout-pay.active:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(0, 212, 255, 0.5);
}

.checkout-pay:disabled {
    cursor: not-allowed;
    transform: none;
}

.topup-rate {
    color: #fff;
    font-size: 0.7rem;
    font-weight: 700;
    margin-top: 0.5rem;
    padding: 5px 12px;
    background: linear-gradient(135deg, rgba(0, 212, 255, 0.15), rgba(0, 212, 255, 0.05));
    border: 1px solid rgba(0, 212, 255, 0.3);
    border-radius: 25px;
    display: inline-flex;
    align-items: center;
    gap: 5px;
}

.topup-rate span {
    color: var(--color-primary);
}

.topup-quick-label {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 2px;
    margin-bottom: 0.5rem;
    margin-top: 0.5rem;
}

.topup-quick {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 6px;
    margin-bottom: 0.75rem;
}

.topup-quick-btn {
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.1);
    color: #aaa;
    font-size: 0.75rem;
    padding: 8px 6px;
    border-radius: 10px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.25s;
}

.topup-quick-btn:hover {
    border-color: rgba(0, 212, 255, 0.5);
    background: rgba(0, 212, 255, 0.08);
    color: #fff;
    transform: translateY(-2px);
}

.topup-quick-btn.active {
    border-color: var(--color-primary);
    background: linear-gradient(135deg, rgba(0, 212, 255, 0.2), rgba(0, 212, 255, 0.1));
    color: var(--color-primary);
    box-shadow: 0 4px 20px rgba(0, 212, 255, 0.2);
}

.btn-topup-submit {
    width: 100%;
    background: linear-gradient(135deg, #00d4ff 0%, #0099cc 100%);
    color: #000;
    border: none;
    padding: 12px 20px;
    border-radius: 12px;
    font-weight: 800;
    font-size: 0.9rem;
    letter-spacing: 2px;
    cursor: pointer;
    transition: all 0.3s;
    position: relative;
    overflow: hidden;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
}

.btn-topup-submit svg {
    width: 20px;
    height: 20px;
    stroke-width: 2.5;
}

.btn-topup-submit::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    transition: left 0.5s;
}

.btn-topup-submit:hover:not(:disabled) {
    transform: translateY(-3px);
    box-shadow: 0 10px 40px rgba(0, 212, 255, 0.4);
}

.btn-topup-submit:hover:not(:disabled)::before {
    left: 100%;
}

.btn-topup-submit:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    background: #333;
}


/* Mobile Menu Button */
.mobile-menu-btn {
    display: none;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 44px;
    height: 44px;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 10px;
    gap: 5px;
    z-index: 10001;
    position: relative;
}

.mobile-menu-btn span {
    display: block;
    width: 24px;
    height: 2px;
    background: #fff;
    border-radius: 2px;
    transition: all 0.3s ease;
}

.mobile-menu-btn.active span:nth-child(1) {
    transform: rotate(45deg) translate(5px, 5px);
}

.mobile-menu-btn.active span:nth-child(2) {
    opacity: 0;
}

.mobile-menu-btn.active span:nth-child(3) {
    transform: rotate(-45deg) translate(5px, -5px);
}

/* Mobile Navigation */
.mobile-nav {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    background: linear-gradient(165deg, #0d1117 0%, #161b22 50%, #0d1117 100%);
    z-index: 10000;
    display: none;
    flex-direction: column;
    justify-content: space-between;
    padding: 100px 35px 35px;
    overflow-y: auto;
    box-sizing: border-box;
}

.mobile-nav::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 200px;
    background: radial-gradient(ellipse at top, rgba(0, 212, 255, 0.08) 0%, transparent 70%);
    pointer-events: none;
}

.mobile-nav.open {
    display: flex;
    animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

.mobile-nav-links {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
    position: relative;
    z-index: 1;
}

.mobile-link {
    font-family: var(--font-display);
    font-size: 1.4rem;
    color: rgba(255, 255, 255, 0.6);
    text-decoration: none;
    letter-spacing: 4px;
    text-transform: uppercase;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    padding: 18px 0;
    position: relative;
    display: flex;
    align-items: center;
    gap: 15px;
    cursor: pointer;
}

.mobile-link::before {
    content: '';
    position: absolute;
    left: -35px;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 0;
    background: var(--color-primary);
    transition: height 0.3s ease;
    box-shadow: 0 0 15px rgba(0, 212, 255, 0.8);
}

.mobile-link::after {
    content: '';
    position: absolute;
    bottom: 12px;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, var(--color-primary), transparent);
    transition: width 0.4s ease;
}

.mobile-link:hover::before,
.mobile-link.active::before {
    height: 30px;
}

.mobile-link:hover::after,
.mobile-link.active::after {
    width: 60px;
}

.mobile-link:hover,
.mobile-link.active {
    color: #fff;
    text-shadow: 0 0 20px rgba(0, 212, 255, 0.5);
    transform: translateX(10px);
}

.mobile-link.market {
    color: var(--color-primary);
    text-shadow: 0 0 15px rgba(0, 212, 255, 0.4);
}

.mobile-link.market.router-link-active {
    color: var(--color-primary);
    text-shadow: 0 0 15px rgba(0, 212, 255, 0.4);
}

.mobile-link.market::before {
    height: 30px;
}

.mobile-link.market::after {
    width: 50px;
}

/* Mobile Bottom Section */
.mobile-bottom {
    margin-top: auto;
    padding-top: 30px;
    position: relative;
    z-index: 1;
}

.mobile-bottom::before {
    content: '';
    position: absolute;
    top: 0;
    left: -35px;
    right: -35px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.3), transparent);
}

.mobile-user-section {
    display: flex;
    flex-direction: column;
    gap: 18px;
}

/* User Card */
.mobile-user-card {
    display: flex;
    align-items: center;
    gap: 16px;
    text-decoration: none;
    padding: 18px 22px;
    background: linear-gradient(135deg, rgba(0, 212, 255, 0.05) 0%, rgba(0, 150, 200, 0.02) 100%);
    border: 1px solid rgba(0, 212, 255, 0.15);
    border-radius: 18px;
    transition: all 0.4s ease;
    position: relative;
    overflow: hidden;
}

.mobile-user-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.1), transparent);
    transition: left 0.5s ease;
}

.mobile-user-card:hover::before {
    left: 100%;
}

.mobile-user-card:hover {
    border-color: rgba(0, 212, 255, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(0, 212, 255, 0.15);
}

.mobile-user-avatar {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    border: 2px solid rgba(0, 212, 255, 0.4);
    box-shadow: 0 0 20px rgba(0, 212, 255, 0.2);
    transition: all 0.3s ease;
}

.mobile-user-card:hover .mobile-user-avatar {
    border-color: var(--color-primary);
    box-shadow: 0 0 25px rgba(0, 212, 255, 0.4);
}

.mobile-user-name {
    font-size: 1.05rem;
    font-weight: 600;
    color: #fff;
    letter-spacing: 0.5px;
}

/* Actions Row */
.mobile-actions-row {
    display: flex;
    align-items: stretch;
    gap: 12px;
}

.mobile-balance {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 16px 20px;
    background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(0, 180, 220, 0.05) 100%);
    border: 1px solid rgba(0, 212, 255, 0.25);
    border-radius: 16px;
    cursor: pointer;
    transition: all 0.4s ease;
    position: relative;
    overflow: hidden;
}

.mobile-balance::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, transparent 0%, rgba(0, 212, 255, 0.1) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
}

.mobile-balance:hover::before {
    opacity: 1;
}

.mobile-tokens {
    text-decoration: none;
    background: linear-gradient(135deg, rgba(56, 189, 248, 0.10) 0%, rgba(56, 189, 248, 0.04) 100%);
    border: 1px solid rgba(56, 189, 248, 0.28);
}
.mobile-tokens .balance-value {
    color: #7dd3fc;
    text-shadow: 0 0 15px rgba(56, 189, 248, 0.3);
}
.mobile-tokens .balance-label {
    color: #38bdf8;
}

.mobile-balance .balance-value {
    font-family: var(--font-display);
    font-size: 1.5rem;
    font-weight: 700;
    color: #fff;
    text-shadow: 0 0 15px rgba(0, 212, 255, 0.3);
    position: relative;
    z-index: 1;
}

.mobile-balance .balance-label {
    font-size: 0.9rem;
    color: var(--color-primary);
    font-weight: 600;
    position: relative;
    z-index: 1;
}

.mobile-balance .balance-plus {
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, var(--color-primary) 0%, #0095b3 100%);
    border-radius: 50%;
    color: #000;
    font-size: 1.3rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    position: relative;
    z-index: 1;
    box-shadow: 0 4px 15px rgba(0, 212, 255, 0.4);
    transition: all 0.3s ease;
}

.mobile-balance:hover .balance-plus {
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(0, 212, 255, 0.5);
}

.mobile-cart {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 60px;
    min-width: 60px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    text-decoration: none;
    color: rgba(255, 255, 255, 0.6);
    position: relative;
    transition: all 0.4s ease;
}

.mobile-cart:hover {
    background: linear-gradient(135deg, rgba(0, 212, 255, 0.15) 0%, rgba(0, 180, 220, 0.08) 100%);
    border-color: rgba(0, 212, 255, 0.4);
    color: var(--color-primary);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 212, 255, 0.15);
}

.mobile-cart svg {
    width: 26px;
    height: 26px;
}

.mobile-cart .cart-count {
    position: absolute;
    top: -6px;
    right: -6px;
    min-width: 22px;
    height: 22px;
    background: linear-gradient(135deg, #ff6b35 0%, #ff4d4d 100%);
    border-radius: 50%;
    font-size: 0.8rem;
    font-weight: 700;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 3px 10px rgba(255, 107, 53, 0.5);
    animation: pulse-badge 2s ease-in-out infinite;
}

@keyframes pulse-badge {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
}

.mobile-login-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    width: 100%;
    padding: 18px 32px;
    background: linear-gradient(135deg, var(--color-primary) 0%, #0095b3 100%);
    border: none;
    border-radius: 14px;
    color: #000;
    font-weight: 700;
    font-size: 0.95rem;
    letter-spacing: 1px;
    cursor: pointer;
    transition: all 0.3s ease;
}

.mobile-login-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(0, 212, 255, 0.3);
}

/* Guest section with cart */
.mobile-guest-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
}

.mobile-cart-guest {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 16px 24px;
    background: rgba(0, 20, 40, 0.6);
    border: 1px solid rgba(var(--color-primary-rgb), 0.2);
    border-radius: var(--radius-md);
    color: var(--color-text);
    text-decoration: none;
    transition: all 0.3s ease;
}

.mobile-cart-guest svg {
    width: 24px;
    height: 24px;
    stroke: var(--color-primary);
}

.mobile-cart-guest .cart-label {
    font-weight: 600;
    font-size: 1rem;
    text-transform: uppercase;
    letter-spacing: 1px;
}

.mobile-cart-guest .cart-count {
    background: var(--color-primary);
    color: #000;
    font-size: 0.75rem;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 10px;
    min-width: 20px;
    text-align: center;
}

.mobile-cart-guest:hover {
    background: rgba(var(--color-primary-rgb), 0.15);
    border-color: var(--color-primary);
    transform: translateY(-2px);
}

/* Compress nav on smaller laptops so it doesn't crowd the right-side controls */
@media (max-width: 1366px) {
    .header {
        padding: var(--space-lg) var(--space-2xl);
    }

    .nav {
        gap: 26px;
    }

    .nav-link {
        letter-spacing: 1.2px;
        font-size: 0.95rem;
    }
}

@media (max-width: 1200px) {
    .header {
        padding: var(--space-lg) var(--space-xl);
    }

    .header-inner {
        gap: 16px;
    }

    .nav {
        gap: 18px;
    }

    .nav-link {
        letter-spacing: 0.6px;
        font-size: 0.88rem;
    }

    .header-right {
        gap: 8px;
    }

    .header-currency-btn,
    .header-balance {
        padding: 7px 10px 7px 12px;
    }

    .hb-amount {
        font-size: 0.92rem;
    }

    .hb-pts {
        font-size: 0.7rem;
    }

    .user-name {
        max-width: 110px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

@media (max-width: 1024px) {
    .desktop-nav {
        display: none !important;
    }
    
    .mobile-menu-btn {
        display: flex !important;
    }
    
    .btn-steam-text {
        display: none;
    }
    
    .btn-steam {
        padding: 10px;
    }
    
    .user-name {
        display: none;
    }
}

@media (max-width: 768px) {
    .desktop-nav {
        display: none !important;
    }
    
    .mobile-menu-btn {
        display: flex !important;
    }
}

@media (max-width: 768px) {
    .header {
        padding: 12px 15px;
    }
    
    .header-inner {
        gap: 10px;
    }
    
    .logo-img {
        height: 38px;
    }
    
    /* Hide these on mobile - they're in the menu */
    .cart-icon,
    .header-balance,
    .header-currency-wrap,
    .user-menu,
    .btn-steam {
        display: none !important;
    }
    
    .mobile-link {
        font-size: 1.3rem;
    }
}

@media (max-width: 480px) {
    .header {
        padding: 10px 12px;
    }
    
    .logo {
        gap: 8px;
    }
    
    .logo-img {
        height: 32px;
    }
    
    .header-balance {
        padding: 6px 10px;
    }
    
    .hb-amount {
        font-size: 0.85rem;
    }
    
    .hb-pts, .hb-plus {
        font-size: 0.65rem;
    }
    
    .cart-icon svg {
        width: 22px;
        height: 22px;
    }
    
    .cart-badge {
        min-width: 16px;
        height: 16px;
        font-size: 0.6rem;
    }
    
    .mobile-menu-btn {
        width: 38px;
        height: 38px;
    }

    .checkout-grid {
        grid-template-columns: 1fr;
    }

    .pay-methods {
        grid-template-columns: 1fr;
    }
}

/* Login Modal */
.login-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.9);
    backdrop-filter: blur(10px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    padding: 20px;
}

.login-modal {
    background: linear-gradient(165deg, rgba(12, 18, 30, 0.98) 0%, rgba(5, 10, 20, 0.99) 100%);
    border: 1px solid rgba(0, 212, 255, 0.2);
    border-radius: 20px;
    padding: 50px 60px;
    width: 100%;
    max-width: 520px;
    position: relative;
    text-align: center;
    box-shadow: 0 0 80px rgba(0, 212, 255, 0.1), 0 30px 60px rgba(0, 0, 0, 0.5);
}

.login-corners {
    position: absolute;
    inset: 15px;
    pointer-events: none;
}

.corner {
    position: absolute;
    width: 20px;
    height: 20px;
    border: 2px solid rgba(0, 212, 255, 0.4);
}

.corner.tl { top: 0; left: 0; border-right: none; border-bottom: none; }
.corner.tr { top: 0; right: 0; border-left: none; border-bottom: none; }
.corner.bl { bottom: 0; left: 0; border-right: none; border-top: none; }
.corner.br { bottom: 0; right: 0; border-left: none; border-top: none; }

.login-close {
    position: absolute;
    top: 20px;
    right: 20px;
    width: 32px;
    height: 32px;
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.4);
    font-size: 28px;
    font-weight: 300;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    z-index: 10;
    line-height: 1;
}

.login-close:hover {
    color: var(--color-primary);
    transform: scale(1.2);
    text-shadow: 0 0 15px rgba(0, 212, 255, 0.5);
}

.login-title {
    font-family: var(--font-display);
    font-size: 2.2rem;
    color: #fff;
    margin-bottom: 35px;
    letter-spacing: 3px;
    text-shadow: 0 0 30px rgba(0, 212, 255, 0.3);
}

.login-checkbox {
    display: flex;
    align-items: flex-start;
    gap: 15px;
    cursor: pointer;
    margin-bottom: 30px;
    text-align: left;
}

.login-checkbox input {
    display: none;
}

.checkbox-custom {
    width: 22px;
    height: 22px;
    min-width: 22px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    margin-top: 2px;
}

.login-checkbox input:checked + .checkbox-custom {
    background: var(--color-primary);
    border-color: var(--color-primary);
}

.login-checkbox input:checked + .checkbox-custom::after {
    content: '✓';
    color: #000;
    font-weight: bold;
    font-size: 14px;
}

.checkbox-text {
    font-size: 0.95rem;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.6;
}

.login-link {
    color: var(--color-primary);
    text-decoration: underline;
    transition: color 0.2s;
}

.login-link:hover {
    color: #fff;
}

.login-steam-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    width: 100%;
    background: linear-gradient(135deg, var(--color-primary) 0%, #0095b3 100%);
    color: #000;
    border: none;
    padding: 18px 30px;
    border-radius: 30px;
    font-weight: 800;
    font-size: 1rem;
    letter-spacing: 2px;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 6px 30px rgba(0, 212, 255, 0.4);
}

.login-steam-btn:hover:not(:disabled) {
    transform: translateY(-3px);
    box-shadow: 0 10px 40px rgba(0, 212, 255, 0.5);
}

.login-steam-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    background: #444;
    box-shadow: none;
}

.login-steam-btn img {
    filter: brightness(0);
}

.login-test-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    padding: 14px 24px;
    margin-top: 10px;
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.6);
    border-radius: 12px;
    font-family: var(--font-display);
    font-size: 0.85rem;
    letter-spacing: 2px;
    cursor: pointer;
    transition: all 0.3s ease;
}

.login-test-btn:hover {
    border-color: rgba(255, 255, 255, 0.4);
    color: #fff;
    background: rgba(255, 255, 255, 0.05);
}

@media (max-width: 768px) {
    .topup-modal {
        padding: 1.5rem;
        max-width: 95%;
    }
    
    .topup-modal.has-checkout {
        padding: 0;
        height: auto;
        max-width: 95%;
        overflow: visible;
    }
    
    .topup-title {
        font-size: 1.5rem;
    }
    
    .topup-quick-grid {
        gap: 10px;
    }
    
    .topup-quick-btn {
        padding: 12px 16px;
        font-size: 0.9rem;
    }
    
    .topup-input {
        padding: 14px;
        font-size: 1.1rem;
    }
    
    .btn-topup-submit {
        padding: 14px;
        font-size: 1rem;
    }
    
    .login-modal {
        padding: 35px 25px;
        max-width: 95%;
    }
    
    .login-title {
        font-size: 1.5rem;
        margin-bottom: 25px;
    }
    
    .login-checkbox {
        font-size: 0.85rem;
    }
    
    .login-steam-btn {
        padding: 14px 24px;
        font-size: 0.9rem;
    }
}

@media (max-width: 480px) {
    .topup-modal {
        padding: 1.25rem;
    }

    .topup-modal.has-checkout {
        padding: 0;
        height: auto;
        overflow: visible;
    }
    
    .topup-title {
        font-size: 1.3rem;
        letter-spacing: 2px;
    }
    
    .topup-label {
        font-size: 0.7rem;
    }
    
    .topup-quick-grid {
        grid-template-columns: repeat(3, 1fr);
        gap: 8px;
    }
    
    .topup-quick-btn {
        padding: 10px 12px;
        font-size: 0.85rem;
    }
    
    .topup-input {
        padding: 12px;
        font-size: 1rem;
    }
    
    .btn-topup-submit {
        padding: 12px;
        font-size: 0.9rem;
        letter-spacing: 2px;
    }
    
    .login-modal {
        padding: 30px 20px;
    }
    
    .login-corners {
        inset: 10px;
    }
    
    .corner {
        width: 15px;
        height: 15px;
    }
    
    .login-title {
        font-size: 1.3rem;
        letter-spacing: 2px;
        margin-bottom: 20px;
    }
    
    .login-checkbox {
        font-size: 0.8rem;
        gap: 10px;
    }
    
    .login-checkbox input[type="checkbox"] {
        width: 18px;
        height: 18px;
    }
    
    .login-steam-btn {
        padding: 12px 20px;
        font-size: 0.85rem;
        letter-spacing: 1px;
    }
}

.login-divider {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 1rem 0;
  color: rgba(255,255,255,0.3);
  font-size: 0.75rem;
}

.login-divider::before,
.login-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: rgba(255,255,255,0.1);
}

.login-email-section {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.login-email-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.15);
  background: rgba(0,0,0,0.4);
  color: #fff;
  font-size: 0.85rem;
  outline: none;
  box-sizing: border-box;
}

.login-email-input:focus {
  border-color: rgba(255,255,255,0.3);
}

.login-email-btn {
  width: 100%;
  padding: 0.75rem;
  border-radius: 8px;
  border: none;
  background: #4a9eff;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  letter-spacing: 0.5px;
}

.login-email-btn:hover:not(:disabled) {
  background: #3a8eef;
}

.login-email-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.login-email-error {
  color: #ff4444;
  font-size: 0.8rem;
  text-align: center;
  padding: 0.5rem;
  background: rgba(255,68,68,0.1);
  border-radius: 6px;
}

.login-mode-toggle {
  background: none;
  border: none;
  color: rgba(255,255,255,0.5);
  font-size: 0.75rem;
  cursor: pointer;
  text-align: center;
  padding: 0.25rem;
}

.login-mode-toggle:hover {
  color: rgba(255,255,255,0.8);
}

.btn-connect-steam {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  border: 1px solid rgba(255,255,255,0.2);
  background: rgba(0,0,0,0.3);
  color: #fff;
  font-size: 0.7rem;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-connect-steam:hover {
  background: rgba(255,255,255,0.1);
}

.user-menu-danger {
  color: #ff4444 !important;
}

.user-menu-delete {
  border-top: 1px solid rgba(255,255,255,0.1);
  padding-top: 0.5rem;
  margin-top: 0.5rem;
}

.delete-form-inline {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 0.5rem;
}

.delete-confirm-btn {
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  border: none;
  background: #ff4444;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 600;
  cursor: pointer;
}

.delete-confirm-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
